import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog'
import { Utensils, Shirt } from 'lucide-react'
import { FoodItemForm } from './FoodItemForm'
import { ClothingItemForm } from './ClothingItemForm'
import type { vendorType, FoodItem, ClothingItem, InventoryItem, Category } from '@/shared/types'

interface AddEditItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorType: vendorType
  mode: 'add' | 'edit'
  item?: InventoryItem | null
  onSave: (item: InventoryItem, imageFile?: File, imageFiles?: File[]) => void
  loading?: boolean
  categories: Category[]
}

export function AddEditItemDialog({
  open,
  onOpenChange,
  vendorType,
  mode,
  item,
  onSave,
  loading = false,
  categories,
}: AddEditItemDialogProps) {
  const [step, setStep] = useState<'choose' | 'form'>(vendorType ? 'form' : 'choose')
  const [itemType, setItemType] = useState<vendorType>(
    vendorType || (item && 'variants' in item ? 'CLOTHING' : 'FOOD')
  )

  useEffect(() => {
    if (open) {
      if (item) {
        const itemTypeFromItem = 'variants' in item ? 'CLOTHING' : 'FOOD'
        setItemType(itemTypeFromItem)
        setStep('form')
      } else if (vendorType) {
        setItemType(vendorType)
        setStep('form')
      } else {
        setStep('choose')
      }
    }
  }, [open, item, vendorType])

  const handleClose = () => {
    if (loading) return
    onOpenChange(false)
    setTimeout(() => {
      setStep(vendorType ? 'form' : 'choose')
      setItemType(vendorType || (item && 'variants' in item ? 'CLOTHING' : 'FOOD'))
    }, 200)
  }

  const title = `${mode === 'add' ? 'Add New' : 'Edit'} ${itemType === 'FOOD' ? 'Food' : 'Clothing'} Item`

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 'choose' ? 'Choose Item Type' : title}</DialogTitle>
        </DialogHeader>

        {step === 'choose' && !vendorType ? (
          <ItemTypeSelector
            onSelect={(type) => {
              setItemType(type)
              setStep('form')
            }}
          />
        ) : itemType === 'FOOD' ? (
          <FoodItemForm
            onCancel={handleClose}
            onSave={(item, imageFile) => onSave(item, imageFile)}
            item={mode === 'edit' ? (item as FoodItem) : undefined}
            loading={loading}
            categories={categories.filter((c) => c.type === 'FOOD')}
          />
        ) : (
          <ClothingItemForm
            onCancel={handleClose}
            onSave={(item, imageFiles) => onSave(item, undefined, imageFiles)}
            item={mode === 'edit' ? (item as ClothingItem) : undefined}
            loading={loading}
            categories={categories.filter((c) => c.type === 'CLOTHING')}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ======================== ITEM TYPE SELECTOR ========================
interface ItemTypeSelectorProps {
  onSelect: (type: vendorType) => void
}

function ItemTypeSelector({ onSelect }: ItemTypeSelectorProps) {
  return (
    <div className="grid grid-cols-2 gap-6 py-8">
      <button
        onClick={() => onSelect('FOOD')}
        className="flex flex-col items-center gap-4 p-8 rounded-xl border-2 hover:border-primary hover:bg-primary/5 transition-all"
      >
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <Utensils className="w-10 h-10 text-primary" />
        </div>
        <span className="text-lg font-medium">Food Item</span>
      </button>
      <button
        onClick={() => onSelect('CLOTHING')}
        className="flex flex-col items-center gap-4 p-8 rounded-xl border-2 hover:border-primary hover:bg-primary/5 transition-all"
      >
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <Shirt className="w-10 h-10 text-primary" />
        </div>
        <span className="text-lg font-medium">Clothing Item</span>
      </button>
    </div>
  )
}
